import {useState, useRef, useEffect, useMemo} from "react";
import axios, {AxiosError} from "axios";

// Contexts
import {useToast} from "../contexts/toast.js";

// Hooks
import useMountedState from "./useMountedState.js";

// Utils
import {getApiBase} from "../utils/apiBase.js";

const API_URL = `${getApiBase()}/api/v1/`;

// Attaches to all requests
axios.defaults.withCredentials = true;

const DEFAULT_MESSAGE =
  "Sorry, that error is on us, please contact support if this wasn't an accident.";

const DETACH_MESSAGE = "Processing request in the background...";

let currentVersion = null;

export async function checkAppVersion() {
  try {
    const res = await fetch("/meta.json", {cache: "no-store"});
    const data = await res.json();

    if (!currentVersion) {
      currentVersion = data.version;
    } else if (currentVersion !== data.version) {
      window.location.reload();
    }
  } catch (e) {
    // version check failed
  }
}

/**
 * @param {String} baseUrl // resource name (e.g. "facilities")
 * @param {Object} suppressMessage // No message
 * @param {Object} suppressError // No message or log to sentry
 * @param {Boolean} redirect // redirect to path
 * @returns {Object}
 */
const useApi = (
  baseUrl,
  options = {suppress: {success: false, error: false, longRequest: true}, redirect: false}
) => {
  const {suppress} = options;

  const isMounted = useMountedState();

  const {addToast} = useToast();

  const url = useRef(`${API_URL}${baseUrl}`);
  // Keep reads and writes on separate controllers so a preview GET (e.g. snapshot
  // change) cannot abort an in-flight PUT/POST and drop the mutation.
  const getController = useRef();
  const mutateController = useRef();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (baseUrl) url.current = `${API_URL}${baseUrl}`;
  }, [baseUrl]);

  useEffect(() => {
    if (isMounted() && data) setLoading(false);
  }, [isMounted, data]);

  /**
   * Process error response
   * @param {Exception} e
   * @returns {string}
   */
  const formatErrorMessage = e => {
    const {response} = e;
    if (typeof e === "string") return e;
    if (e instanceof Error || e instanceof AxiosError) return response?.data?.message || e.message;
    return DEFAULT_MESSAGE;
  };

  /**
   * @param {Number} id // resource name (e.g. "facilities")
   * @param {Object} params // axios url params overriding global params
   * @param {Object} args // additional arguments for axios config
   * @returns {Object}
   */
  const callGet = async (id, params, args) => {
    try {
      checkAppVersion();
      setLoading(true);

      // Only abort prior GETs — never an in-flight mutation.
      if (getController.current) getController.current.abort();

      if (!args?.signal) {
        getController.current = new AbortController();
        args = {...args, signal: getController.current.signal};
      }

      const response = await axios.get(id ? `${url.current}/${id}` : url.current, {
        params,
        ...args
      });

      if (isMounted() && response && response.data)
        setData(Array.isArray(response.data) ? [...response.data] : {...response.data});

      return response;
    } catch (e) {
      const toastMessage = formatErrorMessage(e);

      if (suppress && !suppress.error && toastMessage !== "canceled") {
        addToast(toastMessage, "error");
        setLoading(false);
      }

      return toastMessage;
    }
  };

  /**
   * @param {Object} payload
   * @param {Object} args
   * @returns {Object}
   */
  const callPost = async (payload, args) => {
    checkAppVersion();
    setLoading(true);

    let response = null;

    const triggerDetached = setTimeout(() => {
      if (!response && suppress?.longRequest === false) {
        setLoading(false);
        addToast(DETACH_MESSAGE, "info", true);
      }
    }, 3000);

    try {
      if (getController.current) getController.current.abort();
      if (mutateController.current) mutateController.current.abort();
      if (!args?.signal) {
        mutateController.current = new AbortController();
        args = {...args, signal: mutateController.current.signal};
      }

      response = await axios.post(url.current, payload, args);

      if (suppress && !suppress.success && response?.data?.message)
        addToast(response.data.message, "success");

      if (isMounted() && response?.data)
        setData(Array.isArray(response.data) ? [response.data] : {...response.data.data});

      return response;
    } catch (e) {
      clearTimeout(triggerDetached);

      const toastMessage = formatErrorMessage(e);

      if (suppress && !suppress.error && toastMessage !== "canceled") {
        addToast(toastMessage, "error");
        setLoading(false);
      }

      return toastMessage;
    }
  };

  /**
   * @param {Number} id // resource name (e.g. "facilities")
   * @param {Object} payload // axios url params overriding global params
   * @param {Object} args // additional args
   * @returns {Object}
   */
  const callPut = async (id, payload, args) => {
    checkAppVersion();
    setLoading(true);

    let response = null;

    setTimeout(() => {
      if (!response && suppress?.longRequest === false) {
        setLoading(false);
        addToast(DETACH_MESSAGE);
      }
    }, 2000);

    try {
      if (getController.current) getController.current.abort();
      if (mutateController.current) mutateController.current.abort();
      if (!args?.signal) {
        mutateController.current = new AbortController();
        args = {...args, signal: mutateController.current.signal};
      }

      response = await axios.put(id ? `${url.current}/${id}` : url.current, payload, args);

      if (suppress && !suppress.success && response?.data?.message)
        addToast(response.data.message, "success");

      // Update data to include update
      if (isMounted() && response?.data && data) {
        if (Array.isArray(response.data)) {
          const copy = [...data];
          const index = copy.findIndex(item => item.id === id);
          copy[index] = payload;
          setData(copy);
        } else if (response.data.data) setData({...response.data.data});
      }

      return response;
    } catch (e) {
      const toastMessage = formatErrorMessage(e);

      if (suppress && !suppress.error && toastMessage !== "canceled") {
        addToast(toastMessage, "error");
        setLoading(false);
      }

      return toastMessage;
    }
  };

  /**
   * @param {Number} id // resource name (e.g. "facilities")
   * @param {Object} params // axios url params overriding global params
   * @returns {Object}
   */
  const callPatch = async (id, payload, args = {}) => {
    checkAppVersion();
    setLoading(true);

    let response = null;

    setTimeout(() => {
      if (!response && suppress?.longRequest === false) {
        setLoading(false);
        addToast(DETACH_MESSAGE);
      }
    }, 2000);

    try {
      if (getController.current) getController.current.abort();
      if (mutateController.current) mutateController.current.abort();
      if (!args?.signal) {
        mutateController.current = new AbortController();
        args = {...args, signal: mutateController.current.signal};
      }

      response = await axios.patch(id ? `${url.current}/${id}` : url.current, payload, args);

      if (suppress && !suppress.success && response?.data?.message)
        addToast(response.data.message, "success");

      // Update data to include update
      if (isMounted() && response?.data && data) {
        if (Array.isArray(response.data)) {
          const copy = [...data];
          const index = copy.findIndex(item => item.id === id);
          copy[index] = payload;
          setData(copy);
        } else if (response.data.data) setData({...response.data.data});
      }

      return response;
    } catch (e) {
      const toastMessage = formatErrorMessage(e);

      if (suppress && !suppress.error && toastMessage !== "canceled") {
        addToast(toastMessage, "error");
        setLoading(false);
      }

      return toastMessage;
    }
  };

  /**
   * @param {Number} id // resource name (e.g. "facilities")
   * @param {Object} params // axios url params overriding global params
   * @returns {Object}
   */
  const callDelete = async (id, payload, args = {}) => {
    checkAppVersion();
    setLoading(true);

    let response = null;

    setTimeout(() => {
      if (!response && suppress?.longRequest === false) {
        setLoading(false);
        addToast(DETACH_MESSAGE);
      }
    }, 2000);

    try {
      if (getController.current) getController.current.abort();
      if (mutateController.current) mutateController.current.abort();
      if (!args?.signal) {
        mutateController.current = new AbortController();
        args = {...args, signal: mutateController.current.signal};
      }

      const endpoint = id ? `${url.current}/${id}` : url.current;
      response = await axios.delete(endpoint, payload, args);

      if (suppress && !suppress.success && response?.data?.message)
        addToast(response.data.message, "success");

      // Update data to exclude deleted item
      if (isMounted() && response && data) {
        if (Array.isArray(response.data)) {
          const copy = [...data];
          copy.splice(
            copy.findIndex(item => item.id === id),
            1
          );

          setData(copy);
        } else if (response.data.data) setData({...response.data.data});
      }

      return response;
    } catch (e) {
      const toastMessage = formatErrorMessage(e);

      if (suppress && !suppress.error && toastMessage !== "canceled") {
        addToast(toastMessage, "error");
        setLoading(false);
      }

      return toastMessage;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const api = useMemo(() => ({callGet, callPost, callPut, callPatch, callDelete}), []);

  return {data, api, loading};
};

export default useApi;
